import { History, LocationState, Path } from 'history';
import { TFunction } from 'i18next';
import { useHistory as reactUseHistory } from 'react-router';
import { AVAILABLE_LANGUAGES } from '../i18n/i18n';

/**
 * Translates URL path segments using provided translation function.
 * * FEATURES:
 * - Keeps URL parameters (starting with ':') unchanged.
 * - Normalizes multiple consecutive slashes into a single slash (e.g., '//' -> '/').
 * - Preserves the root empty segments (so '/' stays '/').
 *
 * @param path - URL path to translate (e.g., "/dashboard//settings")
 * @param urlT - TFunction with url translation namespace (e.g., const [urlT] = useTranslation('url'))
 * @returns Translated and normalized URL path
 */
export const translateOne = (path: string, urlT: TFunction): string => {
  const pathWithoutMultipleSlashes = path.replace(/\/+/g, '/');

  return pathWithoutMultipleSlashes
      .split('/')
      .map(segment => {
        if (segment === '' || segment.startsWith(':')) {
          return segment;
        }
        return urlT(segment);
      })
      .join('/');
};

type UrlI18n = {
  getFixedT: (lng: string, ns: string) => TFunction;
};

/**
 * Returns url-namespace translators for every available language.
 * Useful when pathname language may not match the current UI language (e.g. after language switch).
 */
export const getUrlTranslatorsForAllLanguages = (i18n: UrlI18n): TFunction[] =>
  AVAILABLE_LANGUAGES.map(lang => i18n.getFixedT(lang, 'url'));

/**
 * Checks whether pathname contains any language variant of the given url translation key.
 */
export const pathnameIncludesUrlKey = (
  pathname: string,
  urlKey: string,
  urlTranslators: TFunction[],
): boolean =>
  urlTranslators.some(urlT => pathname.includes(urlT(urlKey)));

/**
 * Checks whether any path segment equals a language variant of the given url translation key.
 * Safer than substring matching for short translations (e.g. EN "document").
 */
export const pathnameHasUrlKeySegment = (
  pathname: string,
  urlKey: string,
  urlTranslators: TFunction[],
): boolean => {
  const pathSegments = pathname.split('/').filter(Boolean);
  return urlTranslators.some(urlT => pathSegments.includes(urlT(urlKey)));
};

/**
 * Interface extending LocationState to include an optional systemPath property.
 * This allows us to store the original, untranslated path in the location state when navigating for fallback redirect.
 */
export interface LocationWithSystemPath {
  systemPath?: string
  from?: string
}

/**
 * Custom hook that wraps react-router's useHistory to automatically translate URL paths when pushing new location or replacing it.
 *
 * @param urlT - TFunction with url translation namespace (e.g., const [urlT] = useTranslation('url'))
 */
export function useHistory<HistoryLocationState = (LocationState & LocationWithSystemPath)>(urlT: TFunction): History<HistoryLocationState> {
  const history = reactUseHistory<HistoryLocationState>();

  function getNewState(state: HistoryLocationState | undefined, path: string) {
    return state ? { ...state, systemPath: path } : ({ systemPath: path } as HistoryLocationState);
  }

  const pushTranslatedPath = (path: Path, state?: HistoryLocationState): void => {
    const translatedPath = translateOne(path, urlT);
    history.push(translatedPath, getNewState(state, path));
  };

  const replaceTranslatedPath = (path: Path, state?: HistoryLocationState): void => {
    const translatedPath = translateOne(path, urlT);
    history.replace(translatedPath, getNewState(state, path));
  };

  return { ...history, push: pushTranslatedPath, replace: replaceTranslatedPath };
}
